import { initializeApp, getApp, getApps, FirebaseApp } from 'firebase/app';
import {
  Auth,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  getAuth,
  getRedirectResult,
  setPersistence,
  User,
} from 'firebase/auth';
import { environment } from './environments/environment';
import { useGoogleEmailOtpIdp } from './app/config/email-otp-idp';
import { getFirebaseInitOptions } from './app/config/firebase-config';

export type FirebaseRedirectBootstrapState = {
  user: User | null;
  error: Error | null;
  completed: boolean;
};

let bootstrapState: FirebaseRedirectBootstrapState = {
  user: null,
  error: null,
  completed: false,
};

let bootstrapApp: FirebaseApp | null = null;
let bootstrapAuth: Auth | null = null;
let persistenceReady: Promise<void> | null = null;

export function getFirebaseRedirectBootstrapState(): FirebaseRedirectBootstrapState {
  return bootstrapState;
}

export function getSharedFirebaseApp(): FirebaseApp | null {
  return bootstrapApp;
}

export function getSharedFirebaseAuth(): Auth | null {
  return bootstrapAuth;
}

/** Must run before Angular bootstraps so getRedirectResult() sees the OAuth return. */
export async function bootstrapFirebaseSocialRedirect(): Promise<void> {
  if (!useGoogleEmailOtpIdp() || !environment.firebase?.apiKey) {
    return;
  }
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const firebaseOptions = getFirebaseInitOptions();
    if (!firebaseOptions) {
      return;
    }

    bootstrapApp = getApps().length
      ? getApp()
      : initializeApp(firebaseOptions);
    bootstrapAuth = getAuth(bootstrapApp);
    persistenceReady = setPersistence(bootstrapAuth, browserLocalPersistence);
    await persistenceReady;

    const result = await getRedirectResult(
      bootstrapAuth,
      browserPopupRedirectResolver,
    );
    const user = result?.user ?? bootstrapAuth.currentUser ?? null;
    if (user) {
      try {
        sessionStorage.setItem('he_social_oauth_return', '1');
      } catch {
        // ignore
      }
    }
    bootstrapState = {
      user,
      error: null,
      completed: true,
    };
  } catch (error) {
    bootstrapState = {
      user: null,
      error: error as Error,
      completed: true,
    };
  }
}

export async function awaitFirebasePersistenceReady(): Promise<void> {
  if (persistenceReady) {
    await persistenceReady;
  }
}
